import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import Header from '@/components/Header';
import { api } from '@/lib/apiClient';
import { toast } from 'sonner';

const INITIAL = {
    full_name: '', email: '', business_name: '', role: '',
    structure: '', asset_locked: 'no', sector: '', geography: '', business_description: '',
    grows_beyond_founder: 'no', repeatable_offer: 'no', serve_more_without_cost: 'no', market_reach: 'local',
    revenue_stage: 'none', revenue_type: 'none', has_paying_customers: 'no', customers_returning: 'no',
    churn: 'not_sure', revenue_confidence: 'flat',
    amount_sought: 'under_25k', funding_type: 'not_sure', funding_purpose: 'other',
    urgency: '12_plus_months', open_to_alternatives: 'yes', lead_investor_status: 'no',
    website_url: '', linkedin_url: '', other_links: '', audience_size: 'none', additional_notes: '',
};

const STEPS = ['About you', 'Business structure', 'Scalability', 'Traction', 'Funding goals', 'Optional context'];

export default function Checker() {
    const [step, setStep] = useState(0);
    const [data, setData] = useState(INITIAL);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const canProceed = () => {
        if (step === 0) return data.full_name && data.email && data.business_name && data.role;
        if (step === 1) return data.structure && data.sector && data.geography;
        return true;
    };

    const submit = async () => {
        setSubmitting(true);
        try {
            const res = await api.post('/submissions', data);
            navigate(`/results/${res.data.id}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Something went wrong');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#f8f4ec] grain-bg">
            <Header />
            <div className="max-w-3xl mx-auto px-6 py-12">
                {/* Progress */}
                <div className="flex items-center justify-between mb-8" data-testid="checker-progress">
                    {STEPS.map((s, i) => (
                        <div key={s} className="flex-1 flex items-center gap-2">
                            <div
                                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-mono border ${
                                    i <= step ? 'bg-weave-ink text-weave-cream border-weave-ink' : 'bg-white text-weave-muted border-weave-border'
                                }`}
                            >
                                {i < step ? <Check className="w-3.5 h-3.5" /> : i + 1}
                            </div>
                            {i < STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 ${i < step ? 'bg-weave-ink' : 'bg-weave-sand'}`} />
                            )}
                        </div>
                    ))}
                </div>
                <div className="text-overline text-weave-teal mb-2">Step {step + 1} of {STEPS.length}</div>
                <h1 className="font-display text-4xl md:text-5xl text-weave-ink tracking-tight mb-8">{STEPS[step]}</h1>

                <AnimatePresence mode="wait">
                    <motion.div
                        key={step}
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.25 }}
                        className="bg-white organic-border border border-weave-border p-8 md:p-10 space-y-6"
                    >
                        {step === 0 && <StepAboutYou data={data} set={set} />}
                        {step === 1 && <StepStructure data={data} set={set} />}
                        {step === 2 && <StepScalability data={data} set={set} />}
                        {step === 3 && <StepTraction data={data} set={set} />}
                        {step === 4 && <StepFundingGoals data={data} set={set} />}
                        {step === 5 && <StepOptional data={data} set={set} />}
                    </motion.div>
                </AnimatePresence>

                <div className="mt-8 flex justify-between">
                    <button
                        onClick={() => setStep((s) => Math.max(0, s - 1))}
                        disabled={step === 0}
                        className="pill-btn border border-weave-border text-weave-ink-soft hover:bg-white disabled:opacity-40"
                        data-testid="checker-back-btn"
                    >
                        <ArrowLeft className="w-4 h-4" /> Back
                    </button>
                    {step < STEPS.length - 1 ? (
                        <button
                            onClick={() => setStep((s) => s + 1)}
                            disabled={!canProceed()}
                            className="pill-btn bg-weave-ink text-weave-cream hover:bg-weave-teal-deep disabled:opacity-50"
                            data-testid="checker-next-btn"
                        >
                            Next <ArrowRight className="w-4 h-4" />
                        </button>
                    ) : (
                        <button
                            onClick={submit}
                            disabled={submitting}
                            className="pill-btn bg-weave-teal text-weave-ink hover:opacity-90 disabled:opacity-50"
                            data-testid="checker-submit-btn"
                        >
                            {submitting ? 'Scoring…' : 'Get my score'} <ArrowRight className="w-4 h-4" />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

function Field({ label, children, testId }) {
    return (
        <div className="space-y-2" data-testid={testId}>
            <Label className="text-weave-ink font-medium">{label}</Label>
            {children}
        </div>
    );
}

function YesNo({ value, onChange, name, options = [['yes', 'Yes'], ['no', 'No']] }) {
    return (
        <RadioGroup value={value} onValueChange={onChange} className="flex gap-3 flex-wrap">
            {options.map(([v, l]) => (
                <label
                    key={v}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full border cursor-pointer text-sm transition ${
                        value === v ? 'bg-weave-ink text-weave-cream border-weave-ink' : 'border-weave-border hover:bg-weave-sand'
                    }`}
                    data-testid={`${name}-${v}`}
                >
                    <RadioGroupItem value={v} className="sr-only" />
                    {l}
                </label>
            ))}
        </RadioGroup>
    );
}

const Sel = ({ value, onChange, placeholder, items, testId }) => (
    <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="bg-white" data-testid={testId}>
            <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
            {items.map(([v, l]) => (
                <SelectItem key={v} value={v}>{l}</SelectItem>
            ))}
        </SelectContent>
    </Select>
);

function StepAboutYou({ data, set }) {
    return (
        <>
            <Field label="Full name" testId="field-full-name">
                <Input value={data.full_name} onChange={(e) => set('full_name', e.target.value)} placeholder="Jane Smith" data-testid="input-full-name" />
            </Field>
            <Field label="Email address" testId="field-email">
                <Input type="email" value={data.email} onChange={(e) => set('email', e.target.value)} placeholder="jane@company.co.uk" data-testid="input-email" />
            </Field>
            <Field label="Business name" testId="field-business-name">
                <Input value={data.business_name} onChange={(e) => set('business_name', e.target.value)} placeholder="Acme Impact Ltd" data-testid="input-business-name" />
            </Field>
            <Field label="Your role in the business" testId="field-role">
                <Input value={data.role} onChange={(e) => set('role', e.target.value)} placeholder="Founder / CEO" data-testid="input-role" />
            </Field>
        </>
    );
}

function StepStructure({ data, set }) {
    return (
        <>
            <Field label="What is your business structure?" testId="field-structure">
                <Sel
                    value={data.structure}
                    onChange={(v) => set('structure', v)}
                    placeholder="Select structure"
                    testId="select-structure"
                    items={[
                        ['limited_company', 'Limited company with shares'],
                        ['cic', 'Community Interest Company (CIC)'],
                        ['charity', 'Charity / nonprofit'],
                        ['cooperative', 'Cooperative'],
                        ['sole_trader', 'Sole trader'],
                        ['partnership', 'Partnership'],
                        ['llp', 'LLP'],
                        ['not_incorporated', 'Not yet incorporated'],
                        ['other', 'Other'],
                    ]}
                />
            </Field>
            <Field label="Is your organisation asset-locked?" testId="field-asset-locked">
                <YesNo
                    value={data.asset_locked}
                    onChange={(v) => set('asset_locked', v)}
                    name="asset-locked"
                    options={[['yes', 'Yes'], ['no', 'No'], ['not_sure', 'Not sure']]}
                />
            </Field>
            <Field label="Sector" testId="field-sector">
                <Input value={data.sector} onChange={(e) => set('sector', e.target.value)} placeholder="e.g. climate tech, health, education" data-testid="input-sector" />
            </Field>
            <Field label="Geography (where do you operate?)" testId="field-geography">
                <Input value={data.geography} onChange={(e) => set('geography', e.target.value)} placeholder="e.g. UK, London, North West" data-testid="input-geography" />
            </Field>
            <Field label="Short business description" testId="field-description">
                <Textarea
                    value={data.business_description}
                    onChange={(e) => set('business_description', e.target.value)}
                    rows={3}
                    placeholder="In 2–3 sentences, what does your business do?"
                    className="bg-white"
                    data-testid="input-description"
                />
            </Field>
        </>
    );
}

function StepScalability({ data, set }) {
    return (
        <>
            <Field label="Is your business designed to grow beyond the founder/team delivering it directly?" testId="field-grows">
                <YesNo value={data.grows_beyond_founder} onChange={(v) => set('grows_beyond_founder', v)} name="grows" />
            </Field>
            <Field label="Is your offer repeatable or standardised?" testId="field-repeatable">
                <YesNo value={data.repeatable_offer} onChange={(v) => set('repeatable_offer', v)} name="repeatable" />
            </Field>
            <Field label="Can you serve more customers without costs increasing at the same rate?" testId="field-cost">
                <YesNo value={data.serve_more_without_cost} onChange={(v) => set('serve_more_without_cost', v)} name="serve-more" />
            </Field>
            <Field label="What market are you targeting?" testId="field-market">
                <Sel
                    value={data.market_reach}
                    onChange={(v) => set('market_reach', v)}
                    placeholder="Select market"
                    testId="select-market"
                    items={[
                        ['local', 'Local'],
                        ['regional', 'Regional'],
                        ['national', 'National'],
                        ['international', 'International'],
                    ]}
                />
            </Field>
        </>
    );
}

function StepTraction({ data, set }) {
    return (
        <>
            <Field label="What stage is your business at?" testId="field-stage">
                <Sel
                    value={data.revenue_stage}
                    onChange={(v) => set('revenue_stage', v)}
                    placeholder="Select stage"
                    testId="select-stage"
                    items={[
                        ['none', 'Pre-revenue / concept only'],
                        ['early', 'Early validation / pilots'],
                        ['paying_inconsistent', 'Paying customers, inconsistent revenue'],
                        ['recurring', 'Recurring or repeatable revenue'],
                        ['strong_growth', 'Strong traction / growth evidence'],
                    ]}
                />
            </Field>
            <Field label="Is your revenue recurring, repeatable, or mostly one-off?" testId="field-revtype">
                <Sel
                    value={data.revenue_type}
                    onChange={(v) => set('revenue_type', v)}
                    placeholder="Select"
                    testId="select-revtype"
                    items={[
                        ['none', 'Not yet generating revenue'],
                        ['one_off', 'Mostly one-off'],
                        ['repeatable', 'Repeatable'],
                        ['recurring', 'Recurring'],
                    ]}
                />
            </Field>
            <Field label="Do you have paying customers, pilots, or signed contracts?" testId="field-paying">
                <YesNo value={data.has_paying_customers} onChange={(v) => set('has_paying_customers', v)} name="paying" />
            </Field>
            <Field label="Are customers returning or renewing?" testId="field-returning">
                <YesNo value={data.customers_returning} onChange={(v) => set('customers_returning', v)} name="returning" />
            </Field>
            <Field label="How would you describe customer churn?" testId="field-churn">
                <Sel
                    value={data.churn}
                    onChange={(v) => set('churn', v)}
                    placeholder="Select"
                    testId="select-churn"
                    items={[
                        ['low', 'Low'],
                        ['moderate', 'Moderate'],
                        ['high', 'High'],
                        ['not_sure', 'Not sure / not applicable'],
                    ]}
                />
            </Field>
            <Field label="If nothing changed, how confident are you revenues will…" testId="field-confidence">
                <Sel
                    value={data.revenue_confidence}
                    onChange={(v) => set('revenue_confidence', v)}
                    placeholder="Select"
                    testId="select-confidence"
                    items={[
                        ['rise', 'Rise'],
                        ['flat', 'Stay flat'],
                        ['fall', 'Fall'],
                    ]}
                />
            </Field>
        </>
    );
}

function StepFundingGoals({ data, set }) {
    return (
        <>
            <Field label="How much funding are you looking for?" testId="field-amount">
                <Sel
                    value={data.amount_sought}
                    onChange={(v) => set('amount_sought', v)}
                    placeholder="Select amount"
                    testId="select-amount"
                    items={[
                        ['under_25k', 'Under £25k'],
                        ['25k_100k', '£25k – £100k'],
                        ['100k_500k', '£100k – £500k'],
                        ['500k_2m', '£500k – £2m'],
                        ['2m_plus', '£2m+'],
                    ]}
                />
            </Field>
            <Field label="What type of funding are you looking for?" testId="field-ftype">
                <Sel
                    value={data.funding_type}
                    onChange={(v) => set('funding_type', v)}
                    placeholder="Select type"
                    testId="select-ftype"
                    items={[
                        ['grant', 'Grant'],
                        ['equity', 'Equity / angel investment'],
                        ['loan', 'Loan / debt finance'],
                        ['accelerator', 'Accelerator / programme'],
                        ['crowdfunding', 'Crowdfunding (equity or reward-based)'],
                        ['revenue_based', 'Revenue-based finance'],
                        ['not_sure', 'Not sure'],
                    ]}
                />
            </Field>
            <Field label="What will the funding be used for?" testId="field-purpose">
                <Sel
                    value={data.funding_purpose}
                    onChange={(v) => set('funding_purpose', v)}
                    placeholder="Select"
                    testId="select-purpose"
                    items={[
                        ['rnd', 'R&D / innovation'],
                        ['product', 'Product development'],
                        ['team', 'Hiring / team'],
                        ['growth', 'Growth / scale-up'],
                        ['working_capital', 'Working capital'],
                        ['market_expansion', 'Market expansion'],
                        ['other', 'Other'],
                    ]}
                />
            </Field>
            <Field label="How soon do you need it?" testId="field-urgency">
                <Sel
                    value={data.urgency}
                    onChange={(v) => set('urgency', v)}
                    placeholder="Select"
                    testId="select-urgency"
                    items={[
                        ['under_3_months', 'Under 3 months'],
                        ['3_6_months', '3–6 months'],
                        ['6_12_months', '6–12 months'],
                        ['12_plus_months', '12+ months'],
                    ]}
                />
            </Field>
            <Field label="Open to alternative funding routes if a better fit is identified?" testId="field-alt">
                <YesNo value={data.open_to_alternatives} onChange={(v) => set('open_to_alternatives', v)} name="alt" />
            </Field>
        </>
    );
}

function StepOptional({ data, set }) {
    return (
        <>
            <Field label="Existing audience or community size" testId="field-audience">
                <Sel
                    value={data.audience_size}
                    onChange={(v) => set('audience_size', v)}
                    placeholder="Select"
                    testId="select-audience"
                    items={[
                        ['none', 'No meaningful audience yet'],
                        ['under_1k', 'Under 1,000 followers / subscribers'],
                        ['1k_10k', '1,000 – 10,000'],
                        ['10k_plus', '10,000+'],
                    ]}
                />
                <p className="text-xs text-weave-muted mt-1">A real signal for crowdfunding fit (across all your channels combined).</p>
            </Field>
            <Field label="Website URL" testId="field-website">
                <Input value={data.website_url} onChange={(e) => set('website_url', e.target.value)} placeholder="https://" data-testid="input-website" />
            </Field>
            <Field label="LinkedIn company page" testId="field-linkedin">
                <Input value={data.linkedin_url} onChange={(e) => set('linkedin_url', e.target.value)} placeholder="https://linkedin.com/company/…" data-testid="input-linkedin" />
            </Field>
            <Field label="Other social links" testId="field-other">
                <Input value={data.other_links} onChange={(e) => set('other_links', e.target.value)} placeholder="Twitter, Instagram, etc." data-testid="input-other-links" />
            </Field>
            <Field label="Anything else we should know?" testId="field-notes">
                <Textarea
                    rows={4}
                    value={data.additional_notes}
                    onChange={(e) => set('additional_notes', e.target.value)}
                    className="bg-white"
                    data-testid="input-notes"
                    placeholder="Recent milestones, grants received, specific asks…"
                />
            </Field>
            <div className="text-xs text-weave-muted leading-relaxed bg-weave-sand rounded-xl p-4">
                A pitch deck isn't required for the score. <strong>1% Club members</strong> can
                run an AI 4Cs deck review as part of their membership at The Weave — separate
                from this checker.
            </div>
        </>
    );
}
